import Vue from 'vue'
import PrePurchaseFarePricesService from 'js/services/api/ticketing/pre-purchase-fare-prices'
import WstrackAmiService from 'js/services/api/ticketing/wstrack-ami'
import TopologyStopsService from 'js/services/api/topology/stops.js'
import TopologyVariantsService from 'js/services/api/topology/variants.js'

import FiltersSection from './_filters-section'
import Fare from '../_fare'
import WstrackFare from './_wstrack-fare'
import FareModal from '../_fare-modal'
import EmptyPricesModal from './_empty-prices-modal'

import template from './index.pug'

// @vue/component
export default Vue.extend({
  template: template(),

  components: {FiltersSection, Fare, WstrackFare, FareModal, EmptyPricesModal},

  props: {
    fares: {
      type: Array,
      default: () => []
    },
    tabIndexInit:null,
  },

  data() {
    return {
      originStops: [],
      destinationStops: [],
      variants: [],
      wstrackFares: [],
      prices: [],
      filters: {},
      selectedFare: null,
      showModal: false,
      showEmptyPricesModal: false
    }
  },

  computed: {
    metersDistance() {
      return this.variant ? this.variant.length : 0
    },

    variant() {
      return this.variants.find(v => v.id === this.filters.variantId)
    },

    originStop() {
      return this.originStops.find(v => v.id === this.filters.originStopId)
    },

    destinationStop() {
      return this.destinationStops.find(v => v.id === this.filters.destinationStopId)
    },

    selectedStops() {
      return this.filters.originStopId && this.filters.destinationStopId
    },

    prePurchaseFareIds() {
      return this.fares.map(f => f.id)
    },

    fare() {
      return this.faresPrices.find(f => f.id === this.selectedFare.id)
    },

    faresPrices() {
      return this.fares.map(fare => {
        const price = this.findPrice(fare.id)
        return {...fare, price}
      })
    },

    filteredFaresPrices() {
      return this.faresPrices.filter(f => f.price)
    },

    orderedFaresPrices() {
      return _.sortBy(this.filteredFaresPrices, 'price')
    }
  },

  watch: {
    'filters.originStopId'() {
      this.reloadDestinationStops()
    },

    'filters.destinationStopId'() {
      this.reloadVariants()
    }
  },

  methods: {
    async getStops(params) {
      const defaultParams = {
        municipality_id: this.$municipality.id,
        with_active_routes: true,
        order_by: 'display_code',
        order_dir: 'asc',
        per: 50
      }

      const response = await TopologyStopsService.index({...defaultParams, ...params})
      return this.orderStopsByDisplayCode(response)
    },

    async getOriginStops(partialName = '') {
      if (partialName.length > 0) {
        this.originStops = await this.getStops({
          has_origin_farezone_variant: true,
          full_name_normalized: partialName
        })
      }
    },

    async getDestinationStops(partialName = '') {
      if (partialName.length > 0) {
        this.destinationStops = await this.getStops({
          full_name_normalized: partialName,
          is_destination_from_origin_fare_zone: this.originStop.fare_zone_id
        })
        // eslint-disable-next-line max-len
        this.destinationStops = this.destinationStops.filter(stop => stop.id !== this.filters.originStopId)
      }
    },

    orderStopsByDisplayCode(stops) {
      return stops.sort((stopA, stopB) => {
        const displayCodeA = parseInt(stopA.display_code)
        const displayCodeB = parseInt(stopB.display_code)

        if (displayCodeA < displayCodeB) { return -1 }
        if (displayCodeA > displayCodeB) { return 1 }
        return 0
      })
    },

    async getVariants() {
      const originStop = this.findStop(this.originStops, this.filters.originStopId)
      const destinationStop = this.findStop(this.destinationStops, this.filters.destinationStopId)

      const params = {
        administrative_area_id: this.$municipality.administrative_area_id,
        origin_farezone_id: originStop.fare_zone_id,
        destination_farezone_id: destinationStop.fare_zone_id,
        per: 0
      }

      const {data} = await TopologyVariantsService.index(params)
      this.variants = data
      if (!this.variants.length) { this.noVariantsNotification() }
    },

    findStop(stopsArray, stopId) {
      return stopsArray.find(stop => stop.id === stopId)
    },

    noVariantsNotification() {
      this.$notifications.send({
        id: 'no-variants-error',
        text: this.$t('prePurchaseFares.notifications.noVariants'),
        type: 'error',
        close: true
      })
    },

    async showFares() {
      await this.getPrices()
      await this.getWstrackFares()
      if (!this.orderedFaresPrices.length && !this.wstrackFares.length) {
        this.toggleEmptyPricesModal()
      }
    },

    showConditions(fare) {
      this.selectedFare = fare
    },

    async getPrices() {
      const params = {
        per: '0',
        pre_purchase_fare_ids: this.prePurchaseFareIds,
        distance: this.metersDistance
      }
      const {data} = await PrePurchaseFarePricesService.index(params)
      this.prices = data
    },

    async getWstrackFares() {
      const params = {
        municipality_id: this.$municipality.id,
        origin_stop_id: this.filters.originStopId,
        destination_stop_id: this.filters.destinationStopId,
        date: moment().format()
      }
      const {data} = await WstrackAmiService.index(params)
      this.wstrackFares = data
    },

    reloadDestinationStops() {
      this.filters.destinationStopId = null
      this.filters.variantId = null
      this.destinationStops = []
      this.variants = []
      if (this.filters.originStopId) { this.getDestinationStops() }
    },

    reloadVariants() {
      this.filters.variantId = null
      this.variants = []
      if (this.selectedStops) { this.getVariants() }
    },

    findPrice(fareId) {
      const price = this.prices.find(p => p.pre_purchase_fare_id === fareId)
      return price ? price.price : 0
    },

    toggleModal(fare = null) {
      this.showModal = !this.showModal
      this.selectedFare = fare
    },

    toggleEmptyPricesModal() {
      this.showEmptyPricesModal = !this.showEmptyPricesModal
    }
  },
  mounted(){
    this.customAccesibilityTabs.setup(this.$options.name)
  },
})
